/**
 * Dropdown component that acts like select for desktop mode and modal for mobile device
 */
import {
  FormControl,
  FormControlProps,
  FormHelperText,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  SelectProps,
  TextFieldProps,
  Typography,
} from '@mui/material';
import classNames from 'classnames';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type DropdownItemValue = string | string[] | number;

export type DropdownItem = {
  title: string;
  value?: DropdownItemValue;
  description?: string;
  selected?: boolean;
  id?: number;
};

export type GroupedDropdownItem = {
  name: string;
  items: DropdownItem[];
};

export enum SelectListSuggestedType {
  AI = 'AI',
}

type Props = {
  items: DropdownItem[];
  groupedItems?: GroupedDropdownItem[];
  selectedItem?: DropdownItem | Record<string, any> | null;
  selectedItems?: DropdownItem[];
  label?: string;
  clearButton?: ReactNode;
  modalTitle?: string;
  includeNone?: boolean;
  translateItems?: boolean;
  translateGroups?: boolean;
  selectProps?: SelectProps;
  textFieldProps?: TextFieldProps;
  formControlProps?: FormControlProps;
  className?: string;
  onSelect?: (item: DropdownItem, itemIndex: number) => void;
  onSelectMultiple?: (item: DropdownItem[]) => void;
  error?: boolean;
  helperText?: ReactNode;
  disabled?: boolean;
  grouped?: boolean;
  displayEmpty?: boolean;
  placeholder?: string;
  placeholderVisibleInMenu?: boolean;
  additionInfo?: string;
  deSelectable?: boolean;
  deselectAction?: () => void;
  multiple?: boolean;
  multipleLimit?: number;
  showDescription?: boolean;
  disableAnimation?: boolean;
  suggestions?: DropdownItem['value'][];
  suggestionType?: SelectListSuggestedType;
  suggestionBannerTitle?: string;
  optionsTitle?: string;
  labelAbove?: boolean;
};

const Dropdown = (props: Props) => {
  const {
    items,
    groupedItems,
    label,
    clearButton,
    selectedItem,
    selectedItems,
    includeNone,
    translateItems,
    translateGroups,
    selectProps,
    formControlProps,
    textFieldProps,
    className,
    onSelect,
    onSelectMultiple,
    error,
    helperText,
    disabled,
    displayEmpty,
    placeholder,
    placeholderVisibleInMenu,
    grouped,
    additionInfo,
    deSelectable,
    deselectAction,
    multiple,
    multipleLimit,
    showDescription,
    disableAnimation,
    suggestions,
    suggestionType = SelectListSuggestedType.AI,
    suggestionBannerTitle,
    optionsTitle,
    labelAbove,
  } = props;
  const { t } = useTranslation();

  const translateOption = translateItems === undefined ? true : translateItems;
  const [groupsOptions, setGroupsOptions] = useState([]);
  const [isMultipleSelectOpen, setIsMultipleSelectOpen] = useState(false);

  const suggestedOptions = useMemo(
    () => items.filter((o) => suggestions?.includes(o?.value)).map((o) => ({ ...o, isSuggestion: true })),
    [items, suggestions],
  );

  const hasSuggestions = suggestedOptions.length > 0;
  const showSuggestionBanner = hasSuggestions && suggestionType === SelectListSuggestedType.AI && suggestionBannerTitle;

  const renderSuggestionsHeader = () =>
    showSuggestionBanner ? (
      <ListSubheader className="dropdown-suggestion-header" disableSticky>
        <div className={classNames('suggestion-content', suggestionType)}>
          <img src="/icons/icn-ai-stars.svg" alt="AI" className="suggestion-icon" />

          <div className="suggestion-title">{suggestionBannerTitle}</div>
        </div>
      </ListSubheader>
    ) : null;

  const renderRadioIcon = useCallback(
    (selected?: boolean) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="20" height="20" rx="10" fill={'transparent'} />
        <rect x="2" y="2" width="20" height="20" rx="10" stroke={selected ? '#FF7D6B' : '#838383'} strokeWidth={selected ? 4 : 1} />
      </svg>
    ),
    [],
  );

  const renderMenuItem = (item: DropdownItem, isSuggestion: boolean, options?: { disabled?: boolean }) => {
    const isSelected = multiple ? selectedItems?.some((selItem) => selItem.value === item.value) : selectedItem?.value === item.value;

    return (
      <MenuItem
        value={item.value}
        key={isSuggestion ? `dropdown-suggestion-${item.value}` : `dropdown-value-${item.value}`}
        disabled={options?.disabled}
        className={classNames('dropdown__item', {
          'dropdown__item--active': isSelected,
          'dropdown__item--suggestion': isSuggestion,
        })}
        onClick={() => {
          if (!multiple && deSelectable && item?.value === selectedItem?.value) {
            if (typeof deselectAction === 'function') {
              deselectAction();

              return;
            }

            onSelect(createDropdownItem(null), -1);
          }
        }}
      >
        <div className="dropdown-item-label">
          {showDescription && item?.description ? (
            <div className="item-with-description">
              {translateOption ? t(item.title) : item.title}
              {translateOption ? <span>{t(item.description)}</span> : <span>{item.description}</span>}
            </div>
          ) : translateOption ? (
            t(item.title)
          ) : (
            item.title
          )}
        </div>

        {isSuggestion && renderRadioIcon(isSelected)}
      </MenuItem>
    );
  };

  const onSelectValue = (itemValue) => {
    const searchableArray = grouped ? groupsOptions : items;
    const foundItemIndex: number = searchableArray.findIndex((item) => item.value === itemValue);

    if (foundItemIndex >= 0) {
      onSelect(searchableArray[foundItemIndex], foundItemIndex);
    } else {
      onSelect(null, -1);
    }
  };

  const onSelectMultipleValues = (event) => {
    const updatedSelectedItems = event.target.value;
    const lastSelected = updatedSelectedItems[updatedSelectedItems.length - 1];

    if (Number.isInteger(lastSelected)) {
      const indexToDelete = updatedSelectedItems.findIndex((item) => item.value === lastSelected);

      if (indexToDelete > -1) {
        updatedSelectedItems.splice(updatedSelectedItems.length - 1, 1);
        updatedSelectedItems.splice(indexToDelete, 1);
      }
    }

    const multipleDropdownItems = updatedSelectedItems.map((dropdownItem) => {
      if (dropdownItem?.value) {
        return dropdownItem;
      }

      const foundItem = items.find((item) => item.value === dropdownItem);

      return foundItem;
    });

    onSelectMultiple(multipleDropdownItems);

    // If user has selected as many values as possible in multi dropdown, we'll automatically close it
    if (multipleDropdownItems.length >= multipleLimit) {
      setIsMultipleSelectOpen(false);
    }
  };

  useEffect(() => {
    if (grouped && groupedItems) {
      let groupedOptionsItems = [];

      groupedItems.forEach((group) => {
        groupedOptionsItems = [...groupedOptionsItems, ...group.items];
      });

      setGroupsOptions(groupedOptionsItems);
    }
  }, []);

  const itemDisabled = (item) => {
    return multipleLimit === selectedItems.length && !selectedItems.some((selItem) => selItem.value === item.value);
  };

  const renderGroup = (group) => {
    const groupItems = group?.items?.map((option) => (
      <MenuItem value={option?.value} key={'dropdown-value-' + option?.value || 'none'}>
        {translateOption ? t(option?.title) : option?.title}
      </MenuItem>
    ));

    return [
      <ListSubheader className={`dropdown-group-${group.name}`} key="dropdown-group">
        {translateGroups ? t(group.name) : group.name}
      </ListSubheader>,
      groupItems,
    ];
  };

  const renderValues = (selectedItems: DropdownItem[]) => {
    if (selectedItems?.length > 0) {
      return selectedItems.map((item) => item.title).join(', ');
    }

    return <span className="placeholder">{placeholder}</span>;
  };

  return (
    <>
      {labelAbove && label && (
        <Typography variant="subtitle1" className="pb-1">
          {label}
        </Typography>
      )}
      <FormControl
        variant={labelAbove ? 'outlined' : 'standard'}
        size={labelAbove ? 'small' : undefined}
        fullWidth={true}
        className={className}
        {...formControlProps}
      >
        {!labelAbove && clearButton ? (
          <div className="dropdown__header">
            {/* LABEL */}
            {label && (
              <InputLabel
                id="dropdown-label"
                error={error}
                shrink={textFieldProps?.InputLabelProps?.shrink}
                disabled={disabled}
                className={`${textFieldProps?.InputLabelProps?.className} dropdown__label dropdown__label--relative`}
                disableAnimation={disableAnimation}
              >
                {label}
              </InputLabel>
            )}
            <div className="dropdown__clear">{clearButton}</div>
          </div>
        ) : (
          !labelAbove &&
          label && (
            <InputLabel
              id="dropdown-label"
              error={error}
              shrink={textFieldProps?.InputLabelProps?.shrink}
              disabled={disabled}
              className={textFieldProps?.InputLabelProps?.className}
              disableAnimation={disableAnimation}
            >
              {label}
            </InputLabel>
          )
        )}

        {multiple && (
          <Select
            labelId="dropdown-label"
            value={selectedItems || []}
            onChange={onSelectMultipleValues}
            fullWidth={true}
            open={isMultipleSelectOpen}
            multiple
            renderValue={renderValues}
            error={error}
            {...selectProps}
            disabled={disabled}
            displayEmpty={true}
            MenuProps={{
              transitionDuration: 0,
              className: 'dropdown-container',
            }}
            onOpen={() => {
              setIsMultipleSelectOpen(true);
            }}
            onClose={() => {
              setIsMultipleSelectOpen(false);
            }}
          >
            {/* SUGGESTIONS SECTION - same as SelectListModal */}
            {hasSuggestions && renderSuggestionsHeader()}
            {hasSuggestions &&
              suggestedOptions.map((item) =>
                renderMenuItem(item, true, {
                  disabled: itemDisabled(item),
                }),
              )}
            {hasSuggestions && <ListSubheader className="dropdown-suggestion-separator" disableSticky />}

            {hasSuggestions && optionsTitle && (
              <ListSubheader className="dropdown-options-title" disableSticky>
                {optionsTitle}
              </ListSubheader>
            )}
            {/*PLACEHOLDER */}
            {placeholder && placeholderVisibleInMenu ? (
              <MenuItem value="placeholder" disabled className="placeholder dropdown__placeholder">
                <span className="placeholder">{placeholder}</span>
              </MenuItem>
            ) : null}
            {/* OPTIONS */}
            {items?.map((item) =>
              renderMenuItem(item, false, {
                disabled: itemDisabled(item),
              }),
            )}
            {/* Addition info */}
            {additionInfo ? (
              <ListSubheader component="div" className="dropdown-info" dangerouslySetInnerHTML={{ __html: additionInfo }} />
            ) : null}
          </Select>
        )}

        {!multiple && (
          <Select
            labelId="dropdown-label"
            value={selectedItem?.value || placeholder || ''}
            onChange={(event) => onSelectValue(event.target.value)}
            fullWidth={true}
            error={error}
            {...selectProps}
            disabled={disabled}
            MenuProps={{
              transitionDuration: 0,
              className: 'dropdown-container',
            }}
            renderValue={(selected) => {
              if (selected === placeholder) {
                return <span className="placeholder">{placeholder}</span>;
              }

              if (selected === selectedItem?.value) {
                return translateOption ? t(selectedItem.title) : selectedItem.title;
              }

              return selected;
            }}
            displayEmpty={displayEmpty}
          >
            {/* SUGGESTIONS SECTION - same structure as SelectListModal: banner + suggested options first */}
            {hasSuggestions && renderSuggestionsHeader()}
            {hasSuggestions && suggestedOptions.map((item) => renderMenuItem(item, true))}
            {hasSuggestions && <ListSubheader className="dropdown-suggestion-separator" disableSticky />}

            {hasSuggestions && optionsTitle && (
              <ListSubheader className="dropdown-options-title" disableSticky>
                {optionsTitle}
              </ListSubheader>
            )}

            {/* EMPTY OPTION */}
            {includeNone ? (
              <MenuItem value="">
                <i>{t('common.unselected')}</i>
              </MenuItem>
            ) : null}

            {/* PLACEHOLDER */}
            {placeholder && placeholderVisibleInMenu ? (
              <MenuItem value={placeholder} disabled={true} className="placeholder">
                <span className="placeholder">{placeholder}</span>
              </MenuItem>
            ) : null}

            {/* OPTIONS - all items including suggested (same as SelectListModal: optionsWithHeaders includes suggested) */}
            {!grouped && items?.map((item) => renderMenuItem(item, false))}

            {/* GROUPED OPTIONS */}
            {grouped && groupedItems?.map(renderGroup)}

            {/* Addition info */}
            {additionInfo ? (
              <ListSubheader component="div" className="dropdown-info" dangerouslySetInnerHTML={{ __html: additionInfo }} />
            ) : null}
          </Select>
        )}

        {/* HELPER TEXT */}
        {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
      </FormControl>
    </>
  );
};

export default Dropdown;

/**
 * Create dropdown item helper
 * @param title
 * @param value
 */
export function createDropdownItem(title: string, value?: DropdownItemValue): DropdownItem {
  // if title and value is the same
  if (title && !value) {
    return {
      title,
      value: title,
    };
  }

  // Title and value exists and they are not the same
  if (title && value) {
    return {
      title,
      value,
    };
  }

  // @ts-ignore, Else return empty object
  return {};
}
